import { hierarchy, tree } from "d3-hierarchy";
import { Degrees, PointAndDirection } from "frontend/utils/transform";
import { OrgChartOrientation, OrgChartElement } from "shared/datamodel/schemas/org-chart";
import * as treeUtils from "../shared/tree-layout-utils";
import { SeparationSiblings } from "../shared/consts";

export interface NodeData {
  id: string;
  x: number;
  y: number;
  descendants: number;
  directChildren: number;
  collapsed: boolean;
  parentId?: string;
  edgeToParent?: any;
  depth?: number;
}

export interface EdgeData {
  childId: string;
  parentId: string;
  parentAnchor: PointAndDirection;
  childAnchor: PointAndDirection;
}

export interface CollapsedButtonData {
  nodeId: string;
  collapsed: boolean;
  x: number;
  y: number;
  direct: number;
  total: number;
}

export interface TreeData {
  nodesData: NodeData[];
  rootNode: NodeData;
  collapseButtonOffset: {
    x: number;
    y: number;
    rotation: number;
  };
  descendants: DescendantsCount;
}

export function computeTreeData(
  layout: OrgChartElement["layout"],
  rootId: string,
  orientation: OrgChartOrientation,
  nodeHeight: number,
  nodeWidth: number,
  separationLayers: number
): TreeData {
  const nodesData = [] as NodeData[];
  let rootNode: NodeData | undefined;

  const layoutUtil = treeUtils.TreeLayoutHelper.from(orientation, nodeWidth, nodeHeight, separationLayers);
  const convert = treeUtils.LayoutProps[orientation].convert;

  const descendants = computeDescendants(layout, rootId);
  const treeLayout = computeTreeLayout(layout, rootId, layoutUtil.getNodeSizeForD3tree());
  const collapseButtonOffset = layoutUtil.getNodeSocket("toLeaves", 12);
  const selfOfs = layoutUtil.getNodeSocket("toRoot", 1);
  const parentOfs = layoutUtil.getNodeSocket("toLeaves", 1);

  treeLayout.eachAfter((node) => {
    const [x, y] = convert(node.x, node.y);
    const { total, direct } = descendants[node.data.id];
    const depth = node.depth;

    // root node is a bit simpler than others, since it has no parent (so no edges up) and can't be dragged
    // inside the tree
    if (node.data.id == rootId) {
      rootNode = {
        id: node.data.id,
        x,
        y,
        descendants: total,
        directChildren: direct,
        collapsed: !node.children,
        depth,
      };
      return;
    }

    // without dragging, things are also simpler
    const nodedata: NodeData = {
      id: node.data.id,
      x,
      y,
      parentId: node.parent?.data.id,
      descendants: total,
      directChildren: direct,
      collapsed: !node.children,
      depth,
    };

    if (node.parent) {
      const [parentX, parentY] = convert(node.parent.x, node.parent.y);
      nodedata.parentId = node.parent.data.id;
      nodedata.edgeToParent = {
        parentAnchor: {
          x: parentX + parentOfs.x,
          y: parentY + parentOfs.y,
          rotation: parentOfs.rotation as Degrees,
        },
        childAnchor: {
          x: x + selfOfs.x,
          y: y + selfOfs.y,
          rotation: selfOfs.rotation as Degrees,
        },
      };
    }
    nodesData.push(nodedata);
  });

  return { nodesData, rootNode: rootNode!, collapseButtonOffset, descendants };
}

interface DescendantsCount {
  [key: string]: { direct: number; total: number };
}

export function computeDescendants(layout: any, rootId: string): DescendantsCount {
  const descendantsCount: DescendantsCount = {};

  // I've see orgcharts that contained invalid data - ids in childrenIds that were not present in the layout
  // A quickfix: filter out invalid ids. The real fix: rewrite org-chart so item has parentId, and not childrenIds
  const root = hierarchy(layout[rootId], (d) => d?.childrenIds?.map((id: any) => layout[id]).filter(Boolean));
  root.sum(() => 1);
  root.each(
    (node) => (descendantsCount[node.data.id] = { direct: node.children?.length ?? 0, total: node.value! - 1 })
  );

  return descendantsCount;
}

export function computeTreeLayout(layout: any, rootId: string, nodeSize: [number, number]) {
  const root = hierarchy(layout[rootId], (d) =>
    !d.childrenIds || d.collapsed ? null : d.childrenIds.map((id: any) => layout[id]).filter(Boolean)
  );
  const treeLayoutMaker = tree<any>()
    .nodeSize(nodeSize)
    .separation(() => SeparationSiblings);
  return treeLayoutMaker(root);
}
